import clsx from "clsx";
import { ArrowUpRight, ArrowDownRight } from "lucide-react";

// 탭 상단 KPI 카드 줄 — 스크롤 없이 "지금 상태"를 5초 안에 파악하게 하는 1층 요약
// (2026-07-18, 대시보드 IA 리서치 반영: F-패턴 상단=핵심 숫자 → 중간=차트 → 하단=표).
//
// 모바일 UX Phase 1(2026-09-21) — opt-in 재구성. 기본 동작은 기존과 100% 동일하다.
//   · prop 미지정 화면 = 종전 그대로(회귀 없음: grid-cols-2 gap-2 sm:gap-3).
//   · mobileLayout="hero" → 모바일 gap 12px + 320px 1열 / 360px+ 2열. desktop(lg) 는 불변.
// 레이아웃 클래스 계산은 lib/kpiLayout.js(순수 JS)로 분리 — node 환경 테스트/검증 스크립트가
// 컴포넌트와 동일한 소스를 쓰게 한다. 기존 import 경로 호환을 위해 re-export 도 유지한다.
import { kpiRowClass, kpiValueClass, kpiCardLayoutClass } from "@/lib/kpiLayout";
export { kpiRowClass, kpiValueClass, kpiCardLayoutClass };

export function KpiRow({ children, cols = 4, mobileLayout }) {
  return <div className={`mb-4 ${kpiRowClass({ cols, mobileLayout })}`}>{children}</div>;
}

// 참고 KPI 접기 — 4개 초과 지표를 "상세 지표 보기"로 접는다(모바일 밀도 완화, opt-in).
// 반드시 "매일 볼 필요 없는" 참고 지표에만 쓴다.
export function KpiMore({ label = "상세 지표 보기", children }) {
  return (
    <details className="group mb-4 rounded-xl bg-ink-50/40">
      <summary className="flex cursor-pointer items-center justify-between px-3 py-2.5 text-[13px] font-medium text-ink-500 [&::-webkit-details-marker]:hidden">
        {label}
        <span className="text-[12px] text-ink-400 transition-transform group-open:rotate-90">▶</span>
      </summary>
      <div className="px-1 pb-1">{children}</div>
    </details>
  );
}

// 색상 의미 체계 고정(QA KPI-001) — 카드마다 임의로 색을 고르지 않고 아래 3가지 톤만 사용한다.
//   good  (민트)  : 매출·정상·증가 방향(충전, 순이익)
//   warn  (오렌지): 비용·차감·주의가 필요한 지표(추정 원가)
//   brand (블루)  : 이 화면의 핵심 지표(hero)
//   default(중립) : 참고용 사용량 지표(호출 수, 토큰 등) — 과도한 강조 지양
// 색 스트로크(테두리) 대신 옅은 톤 배경 + 라벨 옆 색 점(2026-07-18 리디자인) — 색 테두리는
// 카드마다 선이 겹쳐 어수선하고 "AI 티"가 난다는 피드백. 면(배경)은 은은하게 톤만 깔고,
// 의미는 작은 점이 말하게 해서 눈에 띄되 과하지 않게.
const TONE_BG = {
  default: "bg-ink-50/70",
  brand:   "bg-brand-50/70",
  warn:    "bg-amber-50/70",
  good:    "bg-emerald-50/70",
};
const TONE_DOT = {
  default: "bg-ink-300",
  brand:   "bg-brand-500",
  warn:    "bg-amber-500",
  good:    "bg-emerald-500",
};

// deltaPct: 직전 동일 길이 기간 대비 증감률(소수, 0.12 = +12%). null/undefined면 배지 생략
// (비교 데이터가 아직 안 왔거나, 직전 기간이 0이라 비율이 무의미한 경우).
// invert: 지출처럼 "늘어나면 나쁜" 지표는 상승=빨강, 하락=초록으로 색을 뒤집는다.
//
// 모바일 UX Phase 1 opt-in props (미지정 시 기존 동작 유지):
//   mobileHero : 모바일 full-width hero(360px+ 2열 가로지름) + 값 text-2xl.
//   valueTitle : 축약값 표기 시 전체 정확값을 호버/접근성 title 로 노출.
//   deltaBelow : delta 배지를 값 옆이 아니라 아래 줄로(모바일 판독성).
export default function KpiCard({
  label, value, sublabel, tone = "default", hero = false, deltaPct, invert = false,
  mobileHero = false, valueTitle, deltaBelow = false,
}) {
  const toneBg = TONE_BG[tone] || TONE_BG.default;
  const toneDot = TONE_DOT[tone] || TONE_DOT.default;
  let badge = null;
  if (deltaPct != null && Number.isFinite(deltaPct)) {
    const up = deltaPct >= 0;
    const good = invert ? !up : up;
    const Icon = up ? ArrowUpRight : ArrowDownRight;
    badge = (
      <span
        className={`inline-flex shrink-0 items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[11px] font-medium tabular-nums ${
          good ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"
        }`}
        title="직전 동일 길이 기간 대비"
      >
        <Icon className="h-3 w-3" />
        {Math.abs(deltaPct * 100).toFixed(1)}%
      </span>
    );
  }
  const isHero = hero || mobileHero;
  return (
    <div className={clsx(
      "min-w-0 rounded-xl shadow-[0_1px_2px_rgba(15,23,42,0.04)]",
      kpiCardLayoutClass({ mobileHero }),
      toneBg, hero && "shadow-sm",
    )}>
      <div className="flex items-center gap-1.5 text-xs font-medium text-ink-500">
        <span className={clsx("h-1.5 w-1.5 shrink-0 rounded-full", toneDot)} />
        {label}
      </div>
      <div className={clsx("mt-1.5", deltaBelow ? "flex flex-col items-start gap-1" : "flex flex-wrap items-baseline gap-2")}>
        {/* 축약값(valueTitle 지정)은 짧아 줄바꿈 불필요 → break-keep. 기존 화면은 break-all 유지. */}
        <span title={valueTitle} className={kpiValueClass({ isHero, compact: Boolean(valueTitle) })}>
          {value}
        </span>
        {badge}
      </div>
      {sublabel && <div className="mt-1 text-[11px] text-ink-400">{sublabel}</div>}
    </div>
  );
}
